import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { LoginSettingsService, ProfileService } from '@barthauer/abc/core/authentication';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { _AbcDashboardComponent, _AbcGridComponent } from '@barthauer/abc/ui-components';
import { environment } from '../../../environments/environment.prod';
import { DataService, ObjectKeys } from '../services/data.service';
import { NotificationService } from '../services/notification.service';
import { ROLES } from '../models/enumeration-list.model';
import { MeasureGaugeComponent } from './measure-gauge/measure-gauge.component';
import { DataService as CoreDataService, Query, QueryColumn, QueryObjectsModel } from '@barthauer/abc/core/data';
import { Measure, MeasureProgress, SubProject } from '@barthauer/custom/xmt-models';
import { first, Observable } from 'rxjs';
import { DxCircularGaugeModule, DxDataGridModule, DxSelectBoxModule, DxTabPanelModule } from 'devextreme-angular';
import { CommonModule } from '@angular/common';
import { MatrixComponent } from './matrix/matrix.component';
import { AppService } from '../services/app.service';

@Component({
  selector: 'ubt-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  imports: [
    _AbcDashboardComponent,
    MeasureGaugeComponent,
    DxSelectBoxModule,
    DxCircularGaugeModule,
    DxDataGridModule,
    _AbcGridComponent,
    DxTabPanelModule,
    CommonModule,
    MatrixComponent,
  ],
})
export class DashboardComponent implements OnInit {
  protected readonly dataService = inject(DataService);
  private readonly appService = inject(AppService);
  public dashboardId: string;
  public isDashboard: boolean;
  public dashboardEndpoint: string;
  public dashboardAjaxHeader: IAjaxHeader;
  public title: string;
  private allSubProjectsData: SubProject[] = [];
  public subProjectOptions: { id: string; name: string }[] = [{ id: 'all', name: 'All' }];
  public statusOptions: Array<{ name: string; value: string }> = [];
  public selectedSubProject: string = 'all';
  public selectedStatus: string = 'In Arbeit';
  public isMeasureUser: boolean | null = null;
  public filteredMeasures = signal<Measure[]>([]);
  protected gridMeasures = signal<Measure[]>([]);
  protected matrixMeasures = computed(() =>
    this.filteredMeasures().filter((measure) => measure.Benefit != null && measure.Effort != null),
  );
  protected gaugeMeasures = computed(() =>
    this.gridMeasures().filter((measure) => measure.Benefit != null && measure.Effort != null),
  );
  protected totalScoreFilterDataSource = [
    {
      text: this.dataService.res('Ubt-Dashboard-Available-Rating'),
      value: (item: Measure) => item.TotalScore !== null,
    },
    {
      text: this.dataService.res('Ubt-Dashboard-Not-setted-Rating'),
      value: (item: Measure) => item.TotalScore === null,
    },
  ];
  constructor(
    private configService: AbcConfigService,
    private profileService: ProfileService,
    private loginSettingsService: LoginSettingsService,
    private noticationService: NotificationService,
    private coreDataService: CoreDataService,
  ) {
    this.title = this.dataService.res('Ubt-Dashboard-No-Data');
  }
  tabs = [
    { id: 1, title: this.dataService.res('Ubt-Dashboard-Tab-panel-Prioritization') },
    { id: 2, title: this.dataService.res('Ubt-Dashboard-Tab-panel-Management') },
  ];
  async ngOnInit(): Promise<void> {
    this.appService.showLoader(true);
    this.getAllMeasuresData();
    try {
      const currentLanguage = sessionStorage.getItem('currentUserLanguage');
      const profileSettings = await this.profileService.getCurrentProfile().toPromise();
      const loginSettings = await this.loginSettingsService.getCurrentLoginSettings().toPromise();

      this.isMeasureUser = this.dataService.hasAnyRole([ROLES.MeasureManager, ROLES.MeasureParticipant]);

      this.dashboardId = environment.dashboardIds[this.isMeasureUser ? 'measure' : 'admin'][currentLanguage];

      this.dashboardEndpoint = this.configService.getConfiguration().WebApi.ServiceURLs.Dashboard;
      this.dashboardAjaxHeader = {
        Authorization: loginSettings.token_type + ' ' + loginSettings.access_token,
        ProfileId: profileSettings.id,
        P$CompanyId: sessionStorage.getItem('profileAddressId'),
        P$ProjectId: sessionStorage.getItem('projectId'),
        P$ProfilId: sessionStorage.getItem('profileId'),
      };
      this.isDashboard = true;
    } catch (error) {
      console.error(error);
      this.isDashboard = false;
    }
  }

  private getAllMeasuresData(): void {
    this.prepareAllMeasuresData().subscribe((subProjects: SubProject[]) => {
      if(Array.isArray(subProjects) && subProjects.length>0) this.allSubProjectsData = subProjects;
      else this.allSubProjectsData = [];
      this.subProjectOptions = [
        { id: 'all', name: 'All' },
        ...this.allSubProjectsData.map((subProject: SubProject) => ({
          id: subProject.Id,
          name: subProject.Designation,
        })),
      ];
      this.onFilterChanged();
      this.initilaizeStatusOptions();
      this.appService.showLoader(false);
    });
  }
  private initilaizeStatusOptions():void{
    let c_Priorized=0;
    let c_Archived=0;
    let c_Backlog=0;
    this.allSubProjectsData.forEach((project)=>{
      c_Priorized+=project['CountP'];
      c_Archived+=project['CountA'];
      c_Backlog+=project['CountB'];
    });
    this.statusOptions=[
    { name: `${this.dataService.res("Ubt-Measure-List-Archived")} (${c_Archived})`, value: 'Erledigt' },
    { name: `${this.dataService.res("Ubt-Measure-List-Priorized")} (${c_Priorized})`, value: 'In Arbeit' },
    { name: `${this.dataService.res("Ubt-Measure-List-Backlog")} (${c_Backlog})`, value: 'Offen' },
  ];
  }
  private prepareAllMeasuresData(): Observable<SubProject[]> {
    const CompanyId = sessionStorage.getItem('profileAddressId');
    const ProjectId = sessionStorage.getItem('projectId');
    const subProject: Query = new Query();
    subProject.ObjectType = ObjectKeys.SUBPROJECT;

    subProject.OPath = `Project.Address.Id = '${CompanyId}' And Project.Id = IIF(Not(IsNull('${ProjectId}')), '${ProjectId}', Project.Id)`;

    const subProjectColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Id', 'Id'),
      this.coreDataService.createQueryColumn('Count(measures)', 'CountMeasures'),
      this.coreDataService.createQueryColumn('Designation', 'Designation'),
      this.coreDataService.createQueryColumn("Count(measures[Status='In Arbeit'])", 'CountP'),
      this.coreDataService.createQueryColumn("Count(measures[Status='Erledigt'])", 'CountA'),
      this.coreDataService.createQueryColumn("Count(measures[Status='Offen'])", 'CountB')
    ];
    subProject.Columns = subProjectColumns;

    const measures: Query = new Query();
    measures.OPath = 'measures';
    measures.Name = 'measures';

    const measuresColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('Designation', 'Measure_Designation'),
      this.coreDataService.createQueryColumn('Status', 'Status'),
      this.coreDataService.createQueryColumn('36', 'maxScore'),
      this.coreDataService.createQueryColumn('Benefit', 'Benefit'),
      this.coreDataService.createQueryColumn('Effort', 'Effort'),
      this.coreDataService.createQueryColumn('TotalScore', 'TotalScore'),
      this.coreDataService.createQueryColumn("IIF(Impact != null, concat(Impact, ' €'), null)", 'Impact'),
      this.coreDataService.createQueryColumn("IIF(Cost != null, concat(Cost, ' €'), null)", 'Cost'),
    ];
    measures.Columns = measuresColumns;

    const measureTasks: Query = new Query();
    measureTasks.OPath = 'MeasureTasks';
    measureTasks.Name = 'MeasureTasks';

    const measureTasksColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn("CaseWhen(ref:Type, '0', 'Milestone','1','Task')", 'Type'),
      this.coreDataService.createQueryColumn('Subject', 'Subject'),
      this.coreDataService.createQueryColumn('Description', 'Description'),
    ];
    measureTasks.Columns = measureTasksColumns;
    const measureProgresses: Query = new Query();
    measureProgresses.OPath = 'MeasureProgresses';
    measureProgresses.Name = 'MeasureProgresses';

    const measureProgressesColumns: Array<QueryColumn> = [
      this.coreDataService.createQueryColumn('MaturityLevel', 'MaturityLevel'),
      this.coreDataService.createQueryColumn('ProgressPercentage', 'ProgressPercentage'),
      this.coreDataService.createQueryColumn('ProgressDate', 'ProgressDate'),
    ];
    measureProgresses.Columns = measureProgressesColumns;
    measures.ObjectQueries = [measureTasks, measureProgresses];
    subProject.ObjectQueries = [measures];

    const queryModel = new QueryObjectsModel();
    queryModel.ObjectQueries = [subProject];
    return this.coreDataService.executeReadObjectsQuery(queryModel).pipe(first()) as unknown as Observable<
      SubProject[]
    >;
  }
  public onFilterChanged(): void {
    this.applyFilters();
  }
  public applyFilters(): void {
    this.filteredMeasures.set(
      this.allSubProjectsData
        .filter((subProject) => this.selectedSubProject === 'all' || subProject.Id === this.selectedSubProject)
        .flatMap((subProject) => (subProject as SubProject)['measures'] ?? [])
        .filter((measure: Measure) => !this.selectedStatus || measure.Status === this.selectedStatus)
        .map((measure: Measure) => ({ ...measure, ...this.getProgressInfo(measure) })),
    );
    this.gridMeasures.set(this.filteredMeasures());
  }

  public getProgressInfo(measure: Measure): ProgressInfo {
    const progresses: MeasureProgress[] = measure['MeasureProgresses'] ?? [];

    if (!progresses.length) {
      return { maturityLevel: '', progressPercentage: '', progressDirection: '' };
    }

    const sorted = [...progresses].sort(
      (a, b) => new Date(b.ProgressDate).getTime() - new Date(a.ProgressDate).getTime(),
    );

    const latest = sorted[0];
    const maturityLevel = (latest.MaturityLevel ?? '').replace('HG', '');
    const currentProgress = latest.ProgressPercentage ?? 0;

    if (isNaN(currentProgress)) {
      return { maturityLevel, progressPercentage: '', progressDirection: '' };
    }

    let progressPercentage = `${currentProgress}%`;
    let progressDirection = '➡';
    let progressDifference = '';

    if (sorted.length > 1) {
      const previousProgress = sorted[1].ProgressPercentage ?? 0;
      if (previousProgress !== 0) {
        const difference = currentProgress - previousProgress;
        const sign = difference > 0 ? '+' : '-';
        progressDifference = `(${sign}${Math.abs(difference)}%)`;
        progressDirection = difference > 0 ? '↗' : difference < 0 ? '↘' : '➡';
        if (difference === 0) progressDifference = '';
      }
    }
    progressPercentage = `${progressPercentage} ${progressDifference}`.trim();
    return { maturityLevel, progressPercentage, progressDirection };
  }
      public calculateProgressSort = (rowData: ProgressInfo): number => {
        const raw = rowData.progressPercentage ?? '';
        const match = raw.match(/^(\d+(\.\d+)?)/); 
        return match ? parseFloat(match[1]) : 0;
      };
  protected onValuesSelected(measure: Partial<Measure>): void {
    this.gridMeasures.set(
      measure
        ? this.filteredMeasures().filter(
            (filteredMeasure) =>
              filteredMeasure.Effort === measure.Effort && filteredMeasure.Benefit === measure.Benefit,
          )
        : this.filteredMeasures(),
    );
  }
  protected calculateTotalScoreFilterExpression(
    filterValue: (data: Measure) => boolean,
    selectedFilterOperation: string,
  ): [string, string, number] | ((data: Measure) => boolean) {
    if (typeof filterValue === 'function') {
      return filterValue;
    }
    return ['TotalScore', selectedFilterOperation, filterValue];
  }
}


interface IAjaxHeader {
  Authorization: string;
  ProfileId: string;
  P$CompanyId: string;
  P$ProjectId: string;
  P$ProfilId?: string;
}
interface ProgressInfo {
  maturityLevel: string;
  progressPercentage: string;
  progressDirection: string;
}
